// src/pages/monitoring/submissions/SubmitProgressModal.jsx
/**
 * SubmitProgressModal — Multi-step form submit progress dari web.
 *
 * Props:
 *   onClose          () => void
 *   onSuccess        () => void
 *   preselectedScope object | null
 *     — bila diisi, step 1 di-skip dan scope langsung terpilih.
 *     — format: { id, name, code, projectName, scopeType: 'FO'|'TOWER' }
 *     — dipakai ketika tombol ada di dalam segment card (konteks sudah jelas).
 *
 * FLOW:
 *   Dari header halaman (global button):
 *     preselectedScope = null → mulai dari Step 1 (pilih segment/site)
 *
 *   Dari segment card (inline button):
 *     preselectedScope = { ...group } → mulai dari Step 2 (pilih milestone)
 *     Step 1 ditampilkan sebagai "done" di step indicator.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, TrendingUp, ChevronRight, Search, MapPin, Check, Info } from 'lucide-react';
import toast from 'react-hot-toast';

import {
  getAvailableSegments,
  getAvailableSites,
  getLeafMilestonesBySegment,
  getLeafMilestonesBySite,
  submitProgress,
  getMilestoneStatusLabel,
  getMilestoneStatusBg,
} from '../../../services/fieldSubmissionService';

// ============================================================================
// STEP INDICATOR
// ============================================================================

const StepIndicator = ({ current, labels }) => (
  <div className="flex items-center px-6 pt-4 pb-2">
    {labels.map((label, i) => {
      const step   = i + 1;
      const done   = step < current;
      const active = step === current;
      return (
        <React.Fragment key={step}>
          <div className="flex flex-col items-center">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center
              text-xs font-medium border-2 transition-all ${
              done   ? 'bg-blue-700 border-blue-700 text-white'
            : active ? 'bg-blue-50 border-blue-700 text-blue-700'
            :          'bg-gray-100 border-gray-300 text-gray-400'}`}>
              {done ? <Check className="w-3.5 h-3.5" /> : step}
            </div>
            <span className={`text-xs mt-1 whitespace-nowrap ${
              active ? 'text-blue-700 font-medium' : 'text-gray-400'}`}>
              {label}
            </span>
          </div>
          {i < labels.length - 1 && (
            <div className={`flex-1 h-0.5 mx-2 mb-4 rounded ${
              done ? 'bg-blue-700' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ============================================================================
// STEP 1 — Pilih Scope
// ============================================================================

const Step1Scope = ({ scopeType, setScopeType, selectedScope, setSelectedScope }) => {
  const [segments, setSegments] = useState([]);
  const [sites,    setSites]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [search,   setSearch]   = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [segs, sts] = await Promise.all([
          getAvailableSegments(),
          getAvailableSites(),
        ]);
        setSegments(segs);
        setSites(sts);
      } catch {
        toast.error('Gagal memuat data segment/site');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const list     = scopeType === 'FO' ? segments : sites;
  const filtered = list.filter(s =>
    !search
      || (s.name ?? '').toLowerCase().includes(search.toLowerCase())
      || (s.code ?? '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-5 space-y-4">
      {/* Scope type toggle */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-2">Tipe scope</label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { key: 'FO',    label: 'FO Segment',  sub: 'Fiber Optic' },
            { key: 'TOWER', label: 'Tower Site',   sub: 'Tower / BTS' },
          ].map(opt => (
            <button
              key={opt.key}
              onClick={() => { setScopeType(opt.key); setSelectedScope(null); }}
              className={`p-3 rounded-xl border-2 text-left transition-all ${
                scopeType === opt.key
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 bg-gray-50 hover:border-gray-300'}`}>
              <div className={`text-sm font-medium ${
                scopeType === opt.key ? 'text-blue-700' : 'text-gray-700'}`}>
                {opt.label}
              </div>
              <div className={`text-xs ${
                scopeType === opt.key ? 'text-blue-500' : 'text-gray-400'}`}>
                {opt.sub}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={scopeType === 'FO' ? 'Cari segment...' : 'Cari site...'}
          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm
            focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
        />
      </div>

      {/* List */}
      <div className="space-y-2 max-h-52 overflow-y-auto">
        {loading && <p className="text-sm text-gray-400 text-center py-6">Memuat data...</p>}
        {!loading && filtered.map(scope => (
          <button
            key={scope.id}
            onClick={() => setSelectedScope(scope)}
            className={`w-full text-left p-3 rounded-xl border transition-all ${
              selectedScope?.id === scope.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <span className={`text-xs px-1.5 py-0.5 rounded font-mono font-semibold ${
                  scopeType === 'FO'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-purple-100 text-purple-700'}`}>
                  {scope.code}
                </span>
                <p className="text-sm font-medium text-gray-800 mt-1 truncate">{scope.name}</p>
                <p className="text-xs text-gray-400 mt-0.5 truncate">{scope.projectName}</p>
              </div>
              {selectedScope?.id === scope.id && (
                <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center
                  justify-center flex-shrink-0 mt-1">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
          </button>
        ))}
        {!loading && filtered.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">
            {search ? 'Tidak ada hasil' : `Tidak ada ${scopeType === 'FO' ? 'segment' : 'site'} tersedia`}
          </p>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// STEP 2 — Pilih Milestone
// ============================================================================

const Step2Milestone = ({ selectedScope, selectedMilestone, setSelectedMilestone }) => {
  const [milestones, setMilestones] = useState([]);
  const [loading,    setLoading]    = useState(false);

  useEffect(() => {
    if (!selectedScope) return;
    const load = async () => {
      setLoading(true);
      try {
        const fn   = selectedScope.scopeType === 'FO'
          ? getLeafMilestonesBySegment
          : getLeafMilestonesBySite;
        const data = await fn(selectedScope.id);
        setMilestones(data);
      } catch {
        toast.error('Gagal memuat milestone');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedScope]);

  return (
    <div className="p-5">
      {/* Context box — tampilkan scope yang sudah dipilih */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-4
        flex items-start gap-2">
        <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-medium text-blue-800">{selectedScope?.name}</p>
          <p className="text-xs text-blue-600">{selectedScope?.projectName}</p>
        </div>
      </div>

      <label className="block text-xs font-medium text-gray-500 mb-2">
        Pilih milestone <span className="text-red-500">*</span>
      </label>

      <div className="space-y-2 max-h-60 overflow-y-auto">
        {loading && <p className="text-sm text-gray-400 text-center py-8">Memuat milestone...</p>}
        {!loading && milestones.map(ms => (
          <button
            key={ms.id}
            onClick={() => setSelectedMilestone(ms)}
            className={`w-full text-left p-3 rounded-xl border transition-all ${
              selectedMilestone?.id === ms.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-gray-400 font-mono">{ms.itemCode}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full border font-medium
                    ${getMilestoneStatusBg(ms.status)}`}>
                    {getMilestoneStatusLabel(ms.status)}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-800 truncate">{ms.name}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${ms.finalProgress ?? 0}%` }} />
                  </div>
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    {ms.finalProgress ?? 0}%
                  </span>
                </div>
              </div>
              {selectedMilestone?.id === ms.id && (
                <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center
                  justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
          </button>
        ))}
        {!loading && milestones.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">
            Tidak ada milestone aktif untuk scope ini
          </p>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// STEP 3 — Form Detail
// ============================================================================

const Step3Form = ({
  selectedScope, selectedMilestone,
  percentage, setPercentage,
  notes, setNotes,
  latitude, setLatitude,
  longitude, setLongitude,
}) => {
  const lastProgress = selectedMilestone?.finalProgress ?? 0;

  return (
    <div className="p-5 space-y-4">
      {/* Context */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
        <div className="flex gap-6 flex-wrap">
          <div>
            <p className="text-xs text-blue-500">Segment</p>
            <p className="text-sm font-medium text-blue-900 truncate max-w-48">
              {selectedScope?.name}
            </p>
          </div>
          <div>
            <p className="text-xs text-blue-500">Milestone</p>
            <p className="text-sm font-medium text-blue-900">
              {selectedMilestone?.itemCode} — {selectedMilestone?.name}
            </p>
          </div>
        </div>
      </div>

      {/* Progress slider */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium text-gray-500">
            Progress <span className="text-red-500">*</span>
          </label>
          <span className="text-2xl font-bold text-blue-700">{percentage}%</span>
        </div>
        <input
          type="range"
          min={0} max={100} step={1}
          value={percentage}
          onChange={e => setPercentage(Number(e.target.value))}
          className="w-full accent-blue-600"
        />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>0%</span>
          <span className="text-amber-600">
            Progress terakhir diapprove: {lastProgress}%
          </span>
          <span>100%</span>
        </div>
        {percentage < lastProgress && (
          <div className="flex items-center gap-1 mt-1.5 text-xs text-amber-600
            bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
            <Info className="w-3 h-3 flex-shrink-0" />
            Progress lebih rendah dari approval terakhir. Pastikan ini benar.
          </div>
        )}
      </div>

      {/* Notes */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">
          Catatan lapangan
        </label>
        <textarea
          rows={3}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Deskripsi pekerjaan, kondisi lapangan, kendala, dll..."
          className="w-full border border-gray-200 rounded-xl p-3 text-sm
            focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-gray-50"
        />
      </div>

      {/* GPS */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            Koordinat GPS (opsional)
          </span>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="number"
            value={latitude ?? ''}
            onChange={e => setLatitude(e.target.value ? Number(e.target.value) : null)}
            placeholder="Latitude"
            step="any"
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm
              focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
          />
          <input
            type="number"
            value={longitude ?? ''}
            onChange={e => setLongitude(e.target.value ? Number(e.target.value) : null)}
            placeholder="Longitude"
            step="any"
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm
              focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
          />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN MODAL
// ============================================================================

/**
 * @param {object|null} preselectedScope
 *   Bila diisi: skip step 1, langsung ke step 2 (milestone).
 *   Format: { id, name, code, projectName, scopeType: 'FO'|'TOWER' }
 *
 *   Dari header button  → preselectedScope = null  (3 steps)
 *   Dari segment card   → preselectedScope = group  (2 steps, scope sudah terpilih)
 */
export default function SubmitProgressModal({ onClose, onSuccess, preselectedScope = null }) {
  // Bila preselectedScope ada → mulai step 2, scope sudah terpilih
  const initialStep  = preselectedScope ? 2 : 1;
  const initialScope = preselectedScope ?? null;

  const [step,              setStep]              = useState(initialStep);
  const [scopeType,         setScopeType]         = useState(
    preselectedScope?.scopeType === 'TOWER' ? 'TOWER' : 'FO'
  );
  const [selectedScope,     setSelectedScope]     = useState(initialScope);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [percentage,        setPercentage]        = useState(0);

  useEffect(() => {
    if (selectedMilestone) {
      setPercentage(selectedMilestone.finalProgress ?? 0);
    }
  }, [selectedMilestone]);

  const [notes,             setNotes]             = useState('');
  const [latitude,          setLatitude]          = useState(null);
  const [longitude,         setLongitude]         = useState(null);
  const [saving,            setSaving]            = useState(false);

  const STEP_LABELS = ['Segment / Site', 'Milestone', 'Detail'];

  // Total steps yang bisa di-navigate:
  // - Dari header (no preselect): step 1, 2, 3
  // - Dari segment card (preselect): step 2, 3 (step 1 shown as done)
  const minStep = preselectedScope ? 2 : 1;
  const maxStep = 3;

  const canNext = () => {
    if (step === 1) return selectedScope != null;
    if (step === 2) return selectedMilestone != null;
    return true;
  };

  const handleNext = () => {
    if (step < maxStep) setStep(s => s + 1);
  };

  const handleBack = () => {
    if (step > minStep) setStep(s => s - 1);
  };

  const handleSubmit = async () => {
    if (!selectedMilestone) return;
    setSaving(true);
    try {
      await submitProgress(selectedMilestone.id, {
        percentage,
        notes,
        latitude,
        longitude,
      });
      toast.success('Progress berhasil disubmit. Menunggu review PM/PC.');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal submit progress');
    } finally {
      setSaving(false);
    }
  };

  // Label langkah di header modal — sesuaikan dengan step aktif
  const stepLabel = preselectedScope
    ? `Langkah ${step - 1} dari 2`   // step 2→"1 dari 2", step 3→"2 dari 2"
    : `Langkah ${step} dari 3`;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-gray-200 w-full max-w-lg
        flex flex-col max-h-[90vh] overflow-hidden shadow-xl">

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-5 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">Submit Progress</p>
                <p className="text-blue-200 text-xs">{stepLabel}</p>
              </div>
            </div>
            <button onClick={onClose}
              className="text-blue-200 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Step indicator — selalu tampilkan 3 langkah,
            step 1 langsung "done" bila preselectedScope ada */}
        <div className="border-b border-gray-100 flex-shrink-0">
          <StepIndicator current={step} labels={STEP_LABELS} />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {step === 1 && (
            <Step1Scope
              scopeType={scopeType}
              setScopeType={setScopeType}
              selectedScope={selectedScope}
              setSelectedScope={setSelectedScope}
            />
          )}
          {step === 2 && (
            <Step2Milestone
              selectedScope={selectedScope}
              selectedMilestone={selectedMilestone}
              setSelectedMilestone={setSelectedMilestone}
            />
          )}
          {step === 3 && (
            <Step3Form
              selectedScope={selectedScope}
              selectedMilestone={selectedMilestone}
              percentage={percentage}
              setPercentage={setPercentage}
              notes={notes}
              setNotes={setNotes}
              latitude={latitude}
              setLatitude={setLatitude}
              longitude={longitude}
              setLongitude={setLongitude}
            />
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-5 py-3 flex items-center
          justify-between flex-shrink-0 bg-gray-50">
          <div>
            {step > minStep && (
              <button
                onClick={handleBack}
                className="px-4 py-2 text-sm border border-gray-200 rounded-xl
                  hover:bg-gray-100 transition-colors text-gray-600">
                ← Kembali
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
              className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">
              Batal
            </button>
            {step < maxStep ? (
              <button
                onClick={handleNext}
                disabled={!canNext()}
                className="flex items-center gap-2 px-5 py-2 bg-blue-700 text-white
                  text-sm font-medium rounded-xl hover:bg-blue-800 transition-colors
                  disabled:opacity-40 disabled:cursor-not-allowed">
                Lanjut <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-blue-700 text-white
                  text-sm font-medium rounded-xl hover:bg-blue-800 transition-colors
                  disabled:opacity-40">
                <TrendingUp className="w-4 h-4" />
                {saving ? 'Menyimpan...' : 'Submit Progress'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}